// =============================================================================
// ui/state/useJournal.ts — the journal entries, and which date is open.
// -----------------------------------------------------------------------------
// Loaded once and held: a daily journal is a few hundred small rows and every
// surface that touches it (the tab, the calendar, the Coach's Report) wants the
// whole map keyed by date.
//
// ── ⚠ THE OPEN DATE IS SESSION-AWARE, AND SO IS STEPPING IT ──
//
// In session mode the old client steps with `previousSessionDate` /
// `nextSessionDate`, which SKIP WEEKENDS — there is no Saturday session, so ‹ from
// Monday lands on Friday. In calendar mode it steps one calendar day. Getting
// this wrong strands the user on an empty Sunday and makes the arrows look broken.
//
// It also survives a re-render on purpose. The old client keeps `journalDate` in
// module scope because its 15-second background trade sync re-renders the tab —
// and a date held in render state snapped the user back to today every 15 seconds.
// Here that is React state in a provider above the tab, for the same reason.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTabMemory } from './tabMemory';
import { loadJournal } from '../../io/sync/journal';
import type { JournalEntry } from '../../selectors/journal';
import {
  isWeekendIso, nextSessionDate, previousSessionDate, addDays,
} from '../../selectors/journal';
import { usePreferences } from './preferences';
import { useRange } from './range';
import { useSupabase } from './supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type JournalPhase = 'pre' | 'session' | 'recap';

export interface JournalTabState {
  phase: 'loading' | 'ready' | 'failed';
  /** ⚠ NULL until a read succeeds. `{}` means genuinely no entries. */
  byDate: Record<string, JournalEntry> | null;
  error: string | null;
  /** The open journal date. Session-aware; see the header. */
  date: string;
  /** True when the open date has no entry at all. */
  isNewDay: boolean;
  step: (dir: -1 | 1) => void;
  goToday: () => void;
  setDate: (iso: string) => void;
  /** Set when a weekend pick was snapped forward, so the UI can explain it. */
  snappedFrom: string | null;
  activePhase: JournalPhase;
  setPhase: (p: JournalPhase) => void;
  /**
   * The `updated_at` this client last saw for a date, or null.
   *
   * ⚠ Feeds the stale-write check. Null is NOT "no conflict is possible" — it is
   * "we never loaded this row", which is why the writer treats it as fail-open
   * rather than as proof the row is fresh.
   */
  baselineFor: (iso: string) => string | null;
  /**
   * Fold a saved recap back into memory and ADVANCE that date's baseline.
   *
   * ⚠ Only a row that was actually written may advance. A held-back write must
   * keep its stale baseline, or the next attempt sails through the guard and
   * clobbers exactly what the guard just protected.
   */
  applySavedRecap: (iso: string, entry: JournalEntry, updatedAt: string | null) => void;
  /**
   * Fold a saved voice-note list back in and advance that date's baseline.
   *
   * ⚠ Same fold as `applySavedRecap` — entry + baseline, no raw blob — and bound
   * to the same function. It is named separately because the CALL SITE is what
   * makes these reviewable: "the recordings write advanced the baseline" is a
   * sentence you can check, and `applySavedRecap` in the voice-note handler
   * reads like a copy-paste slip.
   */
  applySavedRecordings: (iso: string, entry: JournalEntry, updatedAt: string | null) => void;
  /** The row stored blob for a date, for the plan writer to merge over. */
  rawPlanFor: (iso: string) => Record<string, unknown>;
  /** Fold a saved plan back in and advance that date baseline. */
  applySavedPlan: (iso: string, entry: JournalEntry, rawPlan: Record<string, unknown>, updatedAt: string | null) => void;
  /**
   * The row stored `session_contract` blob, for the contract writer to merge over.
   *
   * ⚠ Separate from `rawPlanFor` rather than one `rawRowFor`: the two writers own
   * different columns and handing either the whole row invites the merge that
   * D62 exists to prevent.
   */
  rawContractFor: (iso: string) => Record<string, unknown>;
  /** Fold a saved contract back in and advance that date baseline. */
  applySavedContract: (iso: string, entry: JournalEntry, rawContract: Record<string, unknown>, updatedAt: string | null) => void;
}

export function useJournalTab(): JournalTabState {
  const { sb } = useSupabase();
  const { prefs } = usePreferences();
  const { today } = useRange();

  const [phase, setLoadPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [byDate, setByDate] = useState<Record<string, JournalEntry> | null>(null);
  const [error, setError] = useState<string | null>(null);
  // ⚠ A Map in a ref-like state slot: replaced wholesale on every advance so React
  // sees a new object. Mutating it in place would leave consumers memoised against
  // a stale baseline, which is silently unguarded.
  const [baseline, setBaseline] = useState<Map<string, string>>(() => new Map());
  const [rawPlans, setRawPlans] = useState<Map<string, Record<string, unknown>>>(() => new Map());
  const [rawContracts, setRawContracts] = useState<Map<string, Record<string, unknown>>>(() => new Map());

  /**
   * ── 🚨 NULL MEANS "THE USER HAS NOT PICKED A DAY", AND NOTHING SEEDS IT ──
   *
   * Until the trader navigates, this stays null and the tab shows `today`
   * through the fallback at `resolved` below. `today` is already the SESSION
   * date in session mode — the same anchor the range filter uses — so the
   * journal and the filter cannot disagree about which day "today" is.
   *
   * ⚠ THERE USED TO BE AN EFFECT HERE THAT SEEDED IT, AND IT WAS A RACE:
   *
   *     useEffect(() => { if (date == null && today) setDateRaw(today); },
   *               [date, today]);
   *
   * `today` and `dayBoundaryMode` both arrive with the preferences read, in one
   * commit. That commit paints the date input — via the FALLBACK, because the
   * effect had not run yet — and schedules this effect with `date === null`
   * captured in its closure. Passive effects flush on a scheduler callback, so
   * there is a real window between the input appearing and the seed landing.
   *
   * A date change inside that window is processed first, `date` becomes the
   * user's pick, and then the STALE closure runs `setDateRaw(today)` over the
   * top of it. The input silently returns to today's date and the pick is gone.
   *
   * It surfaced as a test that failed roughly one run in ten, in two different
   * tests of the same block, always with the same signature — the typed date
   * replaced by the anchor. In the running app it is the same defect, just with
   * a window measured in milliseconds: pick a day the instant the journal
   * paints and it snaps back.
   *
   * ⚠ NO BLACK-BOX TEST CAN CATCH THIS, which is worth saying plainly rather
   * than pretending otherwise. Both designs converge on the same state once the
   * dust settles; they differ only in whether a write can be reordered ahead of
   * a stale read. So the guard is the ABSENCE of the effect, not an assertion —
   * and `step` below carries the fallback that the seed used to provide.
   */
  const [date, setDateRaw] = useTabMemory<string | null>('journal:date', null);
  const [snappedFrom, setSnappedFrom] = useState<string | null>(null);
  const [activePhase, setPhase] = useTabMemory<JournalPhase>('journal:phase', 'pre');

  // ⚠ `=== 'session'` on a possibly-null prefs object reads UNKNOWN as calendar.
  // Safe only because `Journal.tsx` refuses to render the date controls until
  // `dayBoundaryMode` is non-null — nothing here can be driven before then. If a
  // second caller ever appears, this needs to become a three-state check.
  const session = prefs?.dayBoundaryMode === 'session';

  useEffect(() => {
    let cancelled = false;
    setLoadPhase('loading');
    setError(null);

    void (async () => {
      try {
        const res = await loadJournal(sb);
        if (cancelled) return;
        setByDate(res.byDate);
        setBaseline(res.updatedAt);
        setRawPlans(res.rawPlans);
        setRawContracts(res.rawContracts);
        setLoadPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ Stays NULL — never `{}`. An empty map is "you have never journalled",
        // which is a claim, and the wrong one to make on a failed read.
        setError(reportFailure('journal entry', e));
        setLoadPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb]);

  const step = useCallback((dir: -1 | 1) => {
    setSnappedFrom(null);
    setDateRaw((cur) => {
      // ⚠ FALLS BACK TO `today`, and this is what the deleted seeding effect
      // used to provide. The first ‹ or › happens while `date` is still null —
      // the user has not picked a day, they are stepping away from today — and
      // returning `cur` there would make the arrow do nothing on its first
      // press. Reading the anchor at the moment of the step, rather than
      // writing it into state ahead of time, is the whole fix: there is no
      // longer any moment at which this hook writes `today` over a user's pick.
      const base = cur ?? today;
      if (!base) return cur;
      // ⚠ Session mode SKIPS WEEKENDS. Stepping one calendar day here would land
      // the user on a Sunday with nothing on it and no way to tell why.
      if (session) return dir === -1 ? previousSessionDate(base) : nextSessionDate(base);
      return addDays(base, dir);
    });
  }, [session, today]);

  const goToday = useCallback(() => {
    setSnappedFrom(null);
    if (today) setDateRaw(today);
  }, [today]);

  const setDate = useCallback((iso: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
    // ⚠ A weekend pick is SNAPPED FORWARD in session mode, and the fact is
    // surfaced rather than silent. The old client toasts here for exactly that
    // reason: the input appearing to jump on its own reads as a bug.
    if (session && isWeekendIso(iso)) {
      setSnappedFrom(iso);
      setDateRaw(nextSessionDate(iso));
      return;
    }
    setSnappedFrom(null);
    setDateRaw(iso);
  }, [session]);

  const baselineFor = useCallback(
    (iso: string) => baseline.get(iso) ?? null,
    [baseline],
  );

  const applySavedRecap = useCallback(
    (iso: string, entry: JournalEntry, updatedAt: string | null) => {
      setByDate((cur) => ({ ...(cur ?? {}), [iso]: entry }));
      // ⚠ Only advances when the write returned a timestamp. `.select()` on the
      // upsert is what produces it, and without one the row must KEEP its old
      // baseline — an advance to `undefined` reads back as "never loaded", which
      // makes the row permanently fresh and permanently unguarded.
      if (updatedAt) {
        setBaseline((cur) => {
          const next = new Map(cur);
          next.set(iso, updatedAt);
          return next;
        });
      }
    },
    [],
  );

  const rawPlanFor = useCallback(
    (iso: string) => rawPlans.get(iso) ?? {},
    [rawPlans],
  );

  const applySavedPlan = useCallback(
    (iso: string, entry: JournalEntry, rawPlan: Record<string, unknown>, updatedAt: string | null) => {
      setByDate((cur) => ({ ...(cur ?? {}), [iso]: entry }));
      // The blob just written becomes the one the NEXT save merges over. Leaving
      // the old copy here would make a second save re-merge stale keys.
      setRawPlans((cur) => { const n = new Map(cur); n.set(iso, rawPlan); return n; });
      if (updatedAt) {
        setBaseline((cur) => { const n = new Map(cur); n.set(iso, updatedAt); return n; });
      }
    },
    [],
  );

  const rawContractFor = useCallback(
    (iso: string) => rawContracts.get(iso) ?? {},
    [rawContracts],
  );

  const applySavedContract = useCallback(
    (iso: string, entry: JournalEntry, rawContract: Record<string, unknown>, updatedAt: string | null) => {
      setByDate((cur) => ({ ...(cur ?? {}), [iso]: entry }));
      setRawContracts((cur) => { const n = new Map(cur); n.set(iso, rawContract); return n; });
      if (updatedAt) {
        setBaseline((cur) => { const n = new Map(cur); n.set(iso, updatedAt); return n; });
      }
    },
    [],
  );

  /**
   * ── 🚨 A WEEKEND DATE IS SNAPPED HERE TOO, SILENTLY, AND THAT IS BETA'S ──
   *
   * `setDate` already snaps an explicit weekend PICK and toasts about it. That
   * is not enough, and this port shipped only half the rule: CME has no Saturday
   * or Sunday session, so a weekend date is never valid in session mode no
   * matter which path produced it — and the ANCHOR is one of those paths. Load
   * the journal on a Saturday and the old client puts you on Monday; this one
   * left you on the Saturday, on a tab whose entire content is a trading day.
   *
   * Caught by opening the running app on a Saturday to verify something else,
   * which is the only way it could have been: every test in the suite pins a
   * Wednesday.
   *
   * ⚠ SILENT — no `snappedFrom`, so no toast. The old client's comment says why:
   * an initial load on a weekend would otherwise nag every time. The toast
   * belongs to a deliberate pick, where the input appearing to jump on its own
   * is what needs explaining.
   *
   * ⚠ Derived, NOT stored. Writing the snapped value into `date` would be the
   * same mistake D86 has just removed — the resolved day is a function of the
   * anchor and the mode, and both can change under it.
   */
  const picked = date ?? today ?? '';
  const resolved = session && picked && isWeekendIso(picked)
    ? nextSessionDate(picked)
    : picked;

  return useMemo<JournalTabState>(() => ({
    phase, byDate, error,
    date: resolved,
    isNewDay: !!byDate && !byDate[resolved],
    step, goToday, setDate, snappedFrom,
    activePhase, setPhase,
    baselineFor, applySavedRecap, applySavedRecordings: applySavedRecap,
    rawPlanFor, applySavedPlan,
    rawContractFor, applySavedContract,
  }), [phase, byDate, error, resolved, step, goToday, setDate, snappedFrom, activePhase,
    baselineFor, applySavedRecap, rawPlanFor, applySavedPlan,
    rawContractFor, applySavedContract]);
}
